import { useState } from 'react'
import { useField, FormikProps } from 'formik'
import { CloseCircleFilled, DownloadOutlined } from '@ant-design/icons'
import { FileText } from 'react-feather'
import { Typography, message } from 'antd'

import {
  DragFileUploadProps,
  DraggerUploader,
  Button,
  useShowMessage,
  UploadFile,
} from '@pepper/ui'

import { formatBytesToHumanReadableString } from '@pepper/utils/src/function/misc'
import {
  fetchFileWordCount,
  fetchFile,
} from '@pepper/utils/src/api/files/files'

import styles from './_.module.css'
import ComponentPicker from '../ComponentPicker'

interface FieldProps {
  name: string
  label?: React.ReactNode
  required?: boolean
  body?: IProjectBody[]
  actions: FormikProps<any>
  disabled?: boolean
}

/* drag and drop uploader */
interface DragDropFileUploadFieldProps extends FieldProps {
  fieldProps?: Omit<DragFileUploadProps, 'handleFiles'>
  minimal?: boolean
  allowedFiles?: string[]
  info?: React.ReactNode
  maxFileSize?: number
}

interface WordCountResponse {
  status: string
  value: { data: { data: { word_count: number } } }
}

export const DragDropFileUploadField = ({
  name,
  label,
  required,
  fieldProps,
  minimal,
  body,
  actions,
  info,
  maxFileSize,
  allowedFiles,
}: DragDropFileUploadFieldProps): React.ReactElement => {
  const [field, meta] = useField(name)
  const [areAllFilesUploaded, setAreAllFilesUploaded] = useState<
    boolean | undefined
  >(true)

  const checkIsFileUploaded = () => {
    return areAllFilesUploaded && !!(field.value && field.value.length > 0)
  }

  const updateFieldValue = (
    refData: string,
    data: any,
    areAllFilesUploaded?: boolean
  ) => {
    typeof areAllFilesUploaded === 'boolean' &&
      setAreAllFilesUploaded(areAllFilesUploaded)
    if (areAllFilesUploaded) {
      const calculateWordCount = async (_list: any[]) => {
        const promises = _list.map(assignment => {
          if (
            assignment.word_count === 'calculating' &&
            typeof assignment.assignment_file[0] === 'object'
          ) {
            return new Promise((resolve, reject) => {
              fetchFileWordCount(
                assignment.assignment_file[0]?.originFileObj
              ).then(response => {
                if (response.status === 'error') {
                  return reject(response)
                }
                return resolve(response)
              })
            })
          } else {
            return Promise.resolve({
              data: { data: { word_count: assignment.word_count } },
            })
          }
        })
        const results: Record<string, any> = await Promise.allSettled(promises)
        const _updatedList: any[] = results.map(
          (response: WordCountResponse, index: number) => {
            const _assignment = { ..._list[index] }
            if (response.status === 'fulfilled') {
              _assignment.word_count = response?.value.data.data.word_count || 0
            } else {
              _assignment.word_count = 0
            }
            _assignment.name = _assignment.assignment_title
            return _assignment
          }
        )

        const wordCount = _updatedList.reduce(
          (total: number, file) => total + file.word_count,
          0
        )

        // `word_count` is only visible in case there's a validation error
        actions.setFieldValue('word_count', wordCount)

        actions.setFieldValue(refData, _updatedList)
        actions.setFieldValue('indexUpdate', true)
        actions.submitForm()
        message.destroy('wordCountWait')
      }

      message.loading({
        content: 'Please wait...',
        duration: 0,
        key: 'wordCountWait',
      })
      const fieldValue = Array.isArray(data) ? data : [data]
      calculateWordCount(
        fieldValue.map((valueObject: any) => ({
          assignment_file: [valueObject],
          word_count: 'calculating',
          assignment_title: valueObject.name,
        }))
      )
    }
  }

  const defaultFileList = field.value

  return checkIsFileUploaded() ? (
    <TranslationRequirements
      value={field.value[0]}
      body={body}
      actions={actions}
      label={label}
      required={required}
      disabled={fieldProps?.disabled}
    />
  ) : (
    <div className={styles.dragDropWrap}>
      <div className={styles.header}>
        <span className={styles.labelText}>{label}</span>
        {required && <span className={styles.requiredText}>*</span>}
      </div>

      <DraggerUploader
        {...fieldProps}
        minimal={minimal}
        handleFiles={(files, areAllFilesUploaded?: boolean): void => {
          updateFieldValue(name, files, areAllFilesUploaded)
        }}
        defaultFileList={defaultFileList}
        disabled={fieldProps?.disabled}
        info={info}
        maxFileSize={maxFileSize}
        allowedFiles={allowedFiles}
      />

      {meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}

type TranslationRequirementProps = {
  value: any
  label?: React.ReactNode
  required?: boolean
  body?: IProjectBody[]
  actions: FormikProps<any>
  disabled?: boolean
}

const TranslationRequirements = ({
  value,
  body,
  actions,
  label,
  required,
  disabled = false,
}: TranslationRequirementProps) => {
  const showMessage = useShowMessage()

  const handleDownloadFile = async (file: UploadFile) => {
    try {
      let objectURL
      if (file.id) {
        const fileResponse = await fetchFile({ id: `${file.id}` })
        objectURL = URL.createObjectURL(fileResponse.data)

        const link = document.createElement('a')
        link.href = objectURL
        link.setAttribute('download', file.name)
        link.click()
        return
      }
    } catch (error) {
      showMessage({ type: 'error', message: error.message })
    }
  }

  return (
    <>
      <span className={styles.labelText}>
        <span className={styles.labelText}>{label}</span>
        {required && <span className={styles.requiredText}>*</span>}
      </span>

      <ul className={styles.list}>
        <li key={value.uid} className={styles.lineWrap}>
          <Typography.Text
            ellipsis={true}
            title={value.name}
            className={styles.typography}
          >
            <FileText size={16} className={styles.fileText} />
            {value.name}
            {value.size && (
              <span className={styles.listSize}>
                {`(${formatBytesToHumanReadableString(value.size, 0)})`}
              </span>
            )}
          </Typography.Text>
          <span className={styles.iconWrapper}>
            <Button
              size="small"
              type="text"
              className={styles.highlight}
              onClick={() => handleDownloadFile(value)}
              disabled={disabled}
            >
              <DownloadOutlined />
            </Button>
          </span>
        </li>
      </ul>

      {body?.map((bodyItem: any, index: number) => {
        return (
          <div className={styles.subBody} key={index}>
            <ComponentPicker
              actions={actions}
              data={{ ...bodyItem, disabled }}
            />
          </div>
        )
      })}
    </>
  )
}
